import { useCallback, useEffect, useState } from 'react'

import { CroSppCollapsedPackagesVariant } from './useCroSppCollapsedPackagesExperiment'

interface PropertyRoomPackageExpansion {
  activePackagesVariant: CroSppCollapsedPackagesVariant
  packagesExpanded: boolean
  showingMoreRates: boolean
  onTogglePackages: () => void
  onToggleMoreRates: () => void
}

interface PropertyRoomPackageExpansionState {
  initialPackagesExpanded: boolean
  packagesExpanded: boolean
  showingMoreRates: boolean
}

export function shouldInitiallyExpandRoomPackages(
  packagesVariant: CroSppCollapsedPackagesVariant,
): boolean {
  return packagesVariant === CroSppCollapsedPackagesVariant.expanded
}

export function getActiveRoomPackagesVariant(
  packagesVariant: CroSppCollapsedPackagesVariant,
  disabled: boolean,
): CroSppCollapsedPackagesVariant {
  return disabled ? CroSppCollapsedPackagesVariant.control : packagesVariant
}

function usePropertyRoomPackageExpansion(
  packagesVariant: CroSppCollapsedPackagesVariant,
  disabled: boolean,
): PropertyRoomPackageExpansion {
  const activePackagesVariant = getActiveRoomPackagesVariant(packagesVariant, disabled)
  const initialPackagesExpanded = shouldInitiallyExpandRoomPackages(activePackagesVariant)
  const [expansionState, setExpansionState] = useState<PropertyRoomPackageExpansionState>({
    initialPackagesExpanded,
    packagesExpanded: initialPackagesExpanded,
    showingMoreRates: initialPackagesExpanded,
  })

  const stateMatchesActiveVariant = expansionState.initialPackagesExpanded === initialPackagesExpanded
  const packagesExpanded = stateMatchesActiveVariant ? expansionState.packagesExpanded : initialPackagesExpanded
  const showingMoreRates = stateMatchesActiveVariant ? expansionState.showingMoreRates : initialPackagesExpanded

  useEffect(() => {
    if (!stateMatchesActiveVariant) {
      setExpansionState({
        initialPackagesExpanded,
        packagesExpanded: initialPackagesExpanded,
        showingMoreRates: initialPackagesExpanded,
      })
    }
  }, [initialPackagesExpanded, stateMatchesActiveVariant])

  const onTogglePackages = useCallback(() => {
    setExpansionState({
      initialPackagesExpanded,
      packagesExpanded: !packagesExpanded,
      showingMoreRates: packagesExpanded ? showingMoreRates : true,
    })
  }, [initialPackagesExpanded, packagesExpanded, showingMoreRates])

  const onToggleMoreRates = useCallback(() => {
    setExpansionState({
      initialPackagesExpanded,
      packagesExpanded,
      showingMoreRates: !showingMoreRates,
    })
  }, [initialPackagesExpanded, packagesExpanded, showingMoreRates])

  return {
    activePackagesVariant,
    packagesExpanded,
    showingMoreRates,
    onTogglePackages,
    onToggleMoreRates,
  }
}

export default usePropertyRoomPackageExpansion
